import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ProductRawResponse } from "reedy-data/api";

import { useChildSectionState } from "../hooks/useChildSectionState";
import { DataSection } from "./DataSection";
import { ToggleAllButton } from "./ToggleAllButton";
import { humanizeKey, renderFields } from "./renderFields";

function nonEmptyKeys(obj: Record<string, unknown>) {
  return Object.keys(obj).filter((k) => {
    const v = obj[k];
    return v !== null && v !== undefined && v !== "";
  });
}

function renderChildSections(
  obj: Record<string, unknown>,
  testIdPrefix: string,
  openState: Record<string, boolean>,
  onToggle: (key: string) => void,
) {
  return Object.entries(obj)
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([key, value]) => (
      <DataSection
        key={key}
        title={humanizeKey(key)}
        testId={`${testIdPrefix}-${key}`}
        level={1}
        isOpen={!!openState[key]}
        onToggle={() => onToggle(key)}
      >
        {renderFields({ [key]: value })}
      </DataSection>
    ));
}

interface ImportDataSectionProps {
  data: ProductRawResponse;
}

export const ImportDataSection = ({ data }: ImportDataSectionProps) => {
  const { t } = useTranslation(["productDetails"]);

  const baseProductObj = data.baseProduct as unknown as Record<string, unknown>;
  const rawOnixObj = data.rawOnixV3 as unknown as Record<string, unknown>;

  const baseProductKeys = useMemo(
    () => nonEmptyKeys(baseProductObj),
    [baseProductObj],
  );
  const rawOnixKeys = useMemo(() => nonEmptyKeys(rawOnixObj), [rawOnixObj]);

  const baseProductState = useChildSectionState(baseProductKeys);
  const rawOnixState = useChildSectionState(rawOnixKeys);

  return (
    <div className="flex flex-col gap-1">
      <DataSection
        title={t("productDetails:rawData.importData.header")}
        testId="section-header"
      >
        {renderFields(data.header as unknown as Record<string, unknown>)}
      </DataSection>

      <DataSection
        title={t("productDetails:rawData.importData.baseProduct")}
        testId="section-base-product"
        actions={
          <ToggleAllButton
            allOpen={baseProductState.allOpen}
            onToggle={baseProductState.toggleAll}
          />
        }
      >
        {renderChildSections(
          baseProductObj,
          "section-base-product",
          baseProductState.openState,
          baseProductState.toggle,
        )}
      </DataSection>

      <DataSection
        title={t("productDetails:rawData.importData.rawOnixProduct")}
        testId="section-raw-onix"
        actions={
          <ToggleAllButton
            allOpen={rawOnixState.allOpen}
            onToggle={rawOnixState.toggleAll}
          />
        }
      >
        {renderChildSections(
          rawOnixObj,
          "section-raw-onix",
          rawOnixState.openState,
          rawOnixState.toggle,
        )}
      </DataSection>
    </div>
  );
};
